import { useEffect, useMemo, useState } from 'react';

import { Group, Radio, Select, Stack, Text, Textarea, Tooltip } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';

import { useUpdateAppointmentMutation } from '@/store/slices/api/appointmentApiSlice';
import { useGetPatientSchedulesQuery } from '@/store/slices/api/patientsApiSlice';
import {
  useCancelTaskMutation,
  useCreateTaskMutation,
  useLazyListTasksQuery,
  useListTaskTypesQuery,
  useListTasksQuery,
} from '@/store/slices/api/tasksApiSlice';
import { useGetUserSettingsQuery } from '@/store/slices/api/userApiSlice';
import { useAppSelector } from '@/store/hooks';
import { MatchDevice } from '@/types/matchDevice';
import { TaskAlertType, TaskAppointmentType, TaskRequestContext, TaskResponseDto, TaskTransferScope } from '@/types/task';
import { appointmentStatusLabel, appointmentTypeLabel } from '@/utils/appointmentTypeLabel';
import { CATEGORY_LABEL, IN_CLINIC_CPT_OPTIONS, REQUEST_CONTEXT_OPTIONS } from '@/utils/taskConstants';
import CustomModal from '@/ui/Modal';

import { calendarDateIso, existingAppointmentDosError, isoToLocalDate } from './existingAppointmentDos';
import { selectableCreateTaskAppointments } from './selectableCreateTaskAppointments';

type NewApptBillability = 'billable' | 'alert_non_billable';
type ApptKindMode = 'ilr' | 'combo' | 'choice';

// Sentinel value used in the appointment Select to mean "create new"
const NEW_APPT_VALUE = '__new__';

function formatAppointmentDay(value: string | Date): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

interface CreateTaskModalProps {
  opened: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
  organizationId: string;
  devices: MatchDevice[];
  isPatientHfMonitored?: boolean;
  shouldCombineHfRoutine?: boolean;
  patientStatus?: string;
}

function isIlr(device: MatchDevice | undefined): boolean {
  return device?.type?.toLowerCase() === 'ilr';
}

function deviceStatusCode(status: MatchDevice['status']): number | null {
  if (status === undefined || status === null || status === '') return null;
  const named: Record<string, number> = {
    inactive: 0,
    active: 1,
    'new implant': 2,
    pending: 3,
    'entered in error': 4,
  };
  const text = String(status).trim().toLowerCase();
  if (text in named) return named[text];
  const numeric = Number(text);
  return Number.isFinite(numeric) ? numeric : null;
}

/** Active and new-implant devices can be tied to a new task. Inactive devices are also allowed for inactive patients. */
function isSelectableDevice(device: MatchDevice, allowInactive = false): boolean {
  const code = deviceStatusCode(device.status);
  return code === 1 || code === 2 || (allowInactive && code === 0);
}

function isNoShowStatus(status: string | null | undefined): boolean {
  return String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ') === 'no show';
}

function isNoShowTask(task: TaskResponseDto): boolean {
  return task.taskType?.name?.trim().toLowerCase() === 'no show';
}

const OPEN_TASK_STATES = new Set(['open', 'in_progress', 'on_hold']);

async function cancelMatchingNoShowTasks({
  listTasks,
  cancelTask,
  patientId,
  deviceId,
  appointmentId,
}: {
  listTasks: ReturnType<typeof useLazyListTasksQuery>[0];
  cancelTask: ReturnType<typeof useCancelTaskMutation>[0];
  patientId: string;
  deviceId: string | null;
  appointmentId: string;
}): Promise<string> {
  try {
    const matches: TaskResponseDto[] = [];
    let skip = 0;
    for (let page = 0; page < 5; page += 1) {
      const result = await listTasks({
        patientId,
        businessStates: ['open', 'in_progress', 'on_hold'],
        limit: 100,
        skip,
      }).unwrap();
      matches.push(
        ...result.items.filter(
          (task) => isNoShowTask(task) && OPEN_TASK_STATES.has(task.businessState),
        ),
      );
      if (result.items.length < 100) break;
      skip += 100;
    }

    const linked = matches.filter((task) => task.appointmentId === appointmentId);
    const targets =
      linked.length > 0
        ? linked
        : matches.filter((task) => deviceId != null && task.matchDeviceId === deviceId);
    if (targets.length === 0) return '';

    let failed = 0;
    for (const task of targets) {
      try {
        await cancelTask({
          id: task.id,
          body: {
            expectedVersion: task.version,
            reason: 'No-show appointment was selected for a transmission task.',
          },
        }).unwrap();
      } catch {
        failed += 1;
      }
    }
    if (failed > 0) {
      return 'The No Show task could not be canceled. It stays open until someone with task access cancels it.';
    }
    return targets.length === 1
      ? 'The open No Show task was canceled.'
      : `${targets.length} open No Show tasks were canceled.`;
  } catch {
    return 'The No Show task could not be canceled. It stays open until someone with task access cancels it.';
  }
}

export function CreateTaskModal({
  opened,
  onClose,
  patientId,
  patientName,
  organizationId,
  devices,
  isPatientHfMonitored = false,
  shouldCombineHfRoutine = false,
  patientStatus,
}: CreateTaskModalProps) {
  const isPaused = patientStatus?.toUpperCase() === 'PAUSED';
  const isInactive = patientStatus?.toUpperCase() === 'INACTIVE';

  const [taskTypeId, setTaskTypeId] = useState<string | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [setting, setSetting] = useState<'remote' | 'in_clinic' | null>(null);

  // In-clinic fields
  const [dateOfService, setDateOfService] = useState<Date | null>(null);
  const [cptCode, setCptCode] = useState<string | null>(null);

  // Remote appointment selector — holds an appointment ID or NEW_APPT_VALUE
  const [appointmentSelection, setAppointmentSelection] = useState<string | null>(null);
  const createNewAppointment = appointmentSelection === NEW_APPT_VALUE;
  const appointmentId = createNewAppointment ? null : (appointmentSelection ?? null);

  const [existingAppointmentDos, setExistingAppointmentDos] = useState<Date | null>(null);
  const [existingAlertReport, setExistingAlertReport] = useState<'yes' | 'no' | null>(null);
  const [existingAlertType, setExistingAlertType] = useState<'red' | 'yellow' | null>(null);
  const [existingAlertNotes, setExistingAlertNotes] = useState('');

  // Remote — new appointment
  const [newBillability, setNewBillability] = useState<NewApptBillability | null>(null);
  const [newDateOfService, setNewDateOfService] = useState<Date | null>(null);
  const [newAlertReport, setNewAlertReport] = useState<'yes' | 'no' | null>(null);
  const [newAlertType, setNewAlertType] = useState<'red' | 'yellow' | null>(null);
  const [newAlertNonBillableType, setNewAlertNonBillableType] = useState<
    'red' | 'yellow' | 'physician_request' | null
  >(null);
  const [newAlertNotes, setNewAlertNotes] = useState('');
  const [newAppointmentKind, setNewAppointmentKind] = useState<
    'routine' | 'heart failure' | 'combo' | null
  >(null);

  const [note, setNote] = useState('');
  const [requestContext, setRequestContext] = useState<TaskRequestContext | null>(null);
  const [transferScope, setTransferScope] = useState<TaskTransferScope | null>(null);
  const [destinationClinicId, setDestinationClinicId] = useState<string | null>(null);

  const isInternalUser = useAppSelector((state) => state.auth.user?.role?.includes('internal') ?? false);
  const { data: taskTypes = [] } = useListTaskTypesQuery(undefined, { skip: !isInternalUser });
  const transmissionTaskTypeId = taskTypes.find(
    (type) => type.workType === 'clinical' && type.code === 'transmission_tracking'
  )?.id;
  const { data: userSettings } = useGetUserSettingsQuery();
  const [createTask, { isLoading }] = useCreateTaskMutation();
  const [updateAppointment] = useUpdateAppointmentMutation();
  const [cancelTask, { isLoading: isCanceling }] = useCancelTaskMutation();
  const [listTasks] = useLazyListTasksQuery();

  const selectableDevices = devices.filter((d) => isSelectableDevice(d, isInactive));
  const selectedDevice = selectableDevices.find((d) => d.id === deviceId);

  // Derive appointment kind mode whenever a device is selected and billable path is active
  const apptKindMode: ApptKindMode | null = (() => {
    if (!selectedDevice || newBillability !== 'billable') return null;
    if (isIlr(selectedDevice)) return 'ilr';
    if (shouldCombineHfRoutine && isPatientHfMonitored) return 'combo';
    return 'choice';
  })();

  // Same schedule endpoint as the patient page. The API allows 100 rows per call.
  // No-show is requested first. Scheduled also returns pending, from today forward.
  // Projected and future rows are removed below.
  const scheduleSkip = !opened || !patientId || setting !== 'remote' || !deviceId;
  const { data: noShowSchedules } = useGetPatientSchedulesQuery(
    { patientId, page: 1, limit: 100, status: 'no show', order: 'DESC' },
    { skip: scheduleSkip },
  );
  const { data: scheduledSchedules } = useGetPatientSchedulesQuery(
    { patientId, page: 1, limit: 100, status: 'scheduled' },
    { skip: scheduleSkip },
  );
  const { data: transmissionTasks } = useListTasksQuery(
    { patientId, taskTypeIds: [transmissionTaskTypeId ?? ''], limit: 100 },
    { skip: scheduleSkip || !transmissionTaskTypeId }
  );
  const transmissionTaskAppointmentIds = useMemo(() => {
    const ids = new Set<string>();
    for (const task of transmissionTasks?.items ?? []) {
      if (task.businessState === 'canceled') continue;
      if (task.taskType?.code !== 'transmission_tracking') continue;
      if (task.appointmentId) ids.add(task.appointmentId);
    }
    return ids;
  }, [transmissionTasks?.items]);

  const selectableAppointments = useMemo(
    () =>
      selectableCreateTaskAppointments(
        [...(noShowSchedules?.data ?? []), ...(scheduledSchedules?.data ?? [])].map((row) => ({
          id: row.id,
          status: row.status,
          scheduledDate: row.scheduledDate,
          type: row.type,
          deviceId: row.device?.id,
        })),
        deviceId ?? '',
        calendarDateIso(new Date()),
        { excludeAdHoc: true, transmissionTaskAppointmentIds },
      ),
    [noShowSchedules?.data, scheduledSchedules?.data, deviceId, transmissionTaskAppointmentIds],
  );

  // Auto-select kind when mode is fixed (ILR → routine, combo → combo)
  useEffect(() => {
    if (apptKindMode === 'ilr') setNewAppointmentKind('routine');
    else if (apptKindMode === 'combo') setNewAppointmentKind('combo');
    else if (apptKindMode === 'choice') setNewAppointmentKind(null);
  }, [apptKindMode]);

  const resetAll = () => {
    setTaskTypeId(null);
    setDeviceId(null);
    setSetting(null);
    setDateOfService(null);
    setCptCode(null);
    setAppointmentSelection(null);
    setExistingAppointmentDos(null);
    setExistingAlertReport(null);
    setExistingAlertType(null);
    setExistingAlertNotes('');
    setNewBillability(null);
    setNewDateOfService(null);
    setNewAlertReport(null);
    setNewAlertType(null);
    setNewAlertNonBillableType(null);
    setNewAlertNotes('');
    setNewAppointmentKind(null);
    setNote('');
    setRequestContext(null);
    setTransferScope(null);
    setDestinationClinicId(null);
  };

  useEffect(() => {
    if (!opened) resetAll();
  }, [opened]);

  const selectedType = taskTypes.find((t) => t.id === taskTypeId);
  const isTtTask =
    selectedType?.workType === 'clinical' && selectedType?.code === 'transmission_tracking';
  const isPatientOutreach = selectedType?.code === 'patient_outreach';
  const isTransfer = selectedType?.code === 'transfer';
  const isGeneratorChange = selectedType?.code === 'gen_change';
  const requiresDeviceSelection = Boolean(selectedType?.requiresDevice) && !isGeneratorChange;
  const clinicOptions = useMemo(
    () => (userSettings?.clinics ?? [])
      .filter((clinic) => clinic.id && clinic.name && clinic.isClinic !== false && clinic.id !== organizationId)
      .map((clinic) => ({ value: clinic.id, label: clinic.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [organizationId, userSettings?.clinics],
  );
  const destinationClinic = clinicOptions.find((clinic) => clinic.value === destinationClinicId);

  const transferSubject = transferScope === 'internal'
    ? 'Internal transfer'
    : transferScope === 'external'
      ? 'External transfer'
      : selectedType?.name ?? '';
  const subject = isTransfer
    ? transferSubject
    : selectedType
      ? [
          selectedDevice
            ? `${selectedType.name} — ${selectedDevice.type.toUpperCase()} · ${selectedDevice.modelName}`
            : selectedType.name,
          setting === 'in_clinic' ? 'In clinic' : null,
        ]
          .filter(Boolean)
          .join(' — ')
      : '';
  const description = isTransfer && transferScope === 'internal' && destinationClinic
    ? `Destination clinic: ${destinationClinic.label}\n\n${note.trim()}`
    : (note.trim() || undefined);

  // Derive the effective alertType for any path that has one
  const existingEffectiveAlertType: TaskAlertType | null =
    existingAlertReport === 'yes' ? existingAlertType : null;
  const newBillableAlertType: TaskAlertType | null =
    newAlertReport === 'yes' ? newAlertType : null;

  // Whether to show alert notes in each path
  const showExistingAlertNotes = !!existingEffectiveAlertType;
  const showNewBillableAlertNotes = !!newBillableAlertType;
  const showNewNonBillableAlertNotes = !!newAlertNonBillableType;

  const buildPayload = () => {
    const base = {
      taskTypeId: taskTypeId!,
      subject,
      description,
      ...(isPatientOutreach && requestContext ? { requestContext } : {}),
      ...(isTransfer && transferScope ? { transferScope } : {}),
      organizationId,
      patientId,
      ...(deviceId ? { matchDeviceId: deviceId } : {}),
    };

    if (setting === 'in_clinic') {
      return {
        ...base,
        appointmentType: 'in_clinic' as TaskAppointmentType,
        alertType: 'none' as TaskAlertType,
        ...(dateOfService ? { dateOfService: dateOfService.toISOString().split('T')[0] } : {}),
        ...(cptCode ? { cptCode } : {}),
      };
    }

    if (setting === 'remote') {
      if (!createNewAppointment && appointmentId) {
        const alertType: TaskAlertType = existingAlertReport === 'yes'
          ? ((existingAlertType ?? 'red') as TaskAlertType)
          : 'none';
        const appointmentType: TaskAppointmentType = existingAlertReport === 'yes'
          ? (isInactive ? 'alert_non_billable' : 'alert_billable')
          : (isInactive ? 'non_billable' : 'routine_billable');
        return {
          ...base,
          appointmentId,
          appointmentType,
          alertType,
          ...(existingAlertNotes.trim() ? { alertNotes: existingAlertNotes.trim() } : {}),
        };
      }

      if (createNewAppointment) {
        if (newBillability === 'billable') {
          const alertType: TaskAlertType =
            newAlertReport === 'yes' ? ((newAlertType ?? 'red') as TaskAlertType) : 'none';
          return {
            ...base,
            appointmentType: (newAlertReport === 'yes' ? 'alert_billable' : 'routine_billable') as TaskAppointmentType,
            alertType,
            ...(newDateOfService ? { dateOfService: newDateOfService.toISOString().split('T')[0] } : {}),
            ...(newAppointmentKind ? { appointmentKind: newAppointmentKind } : {}),
            ...(newAlertNotes.trim() ? { alertNotes: newAlertNotes.trim() } : {}),
          };
        }

        if (newBillability === 'alert_non_billable') {
          return {
            ...base,
            appointmentType: (newAlertNonBillableType === 'physician_request' ? 'non_billable' : 'alert_non_billable') as TaskAppointmentType,
            alertType: (newAlertNonBillableType ?? 'red') as TaskAlertType,
            ...(newDateOfService ? { dateOfService: newDateOfService.toISOString().split('T')[0] } : {}),
            ...(newAlertNotes.trim() ? { alertNotes: newAlertNotes.trim() } : {}),
          };
        }
      }
    }

    return base;
  };

  const selectedExistingAppointment = selectableAppointments.find((appointment) => appointment.id === appointmentId);
  const originalAppointmentDos = calendarDateIso(selectedExistingAppointment?.scheduledDate);
  const chosenAppointmentDos = calendarDateIso(existingAppointmentDos);
  const appointmentDosError = appointmentId
    ? existingAppointmentDosError(originalAppointmentDos, chosenAppointmentDos, calendarDateIso(new Date()))
    : '';

  const isSubmittable = (() => {
    if (!taskTypeId) return false;
    if (isPatientOutreach && (!requestContext || !note.trim())) return false;
    if (isTransfer) {
      if (!transferScope || !note.trim()) return false;
      if (transferScope === 'internal' && !destinationClinicId) return false;
      return true;
    }
    if (!isTtTask) return !requiresDeviceSelection || Boolean(deviceId);
    if (!deviceId || !setting) return false;
    if (isPaused && setting === 'in_clinic') return false;
    if (setting === 'in_clinic') return true;
    if (setting === 'remote') {
      if (!createNewAppointment) {
        // Existing appointment — allowed even for paused patients
        if (!appointmentId || Boolean(appointmentDosError)) return false;
        if (existingAlertReport === null) return false;
        if (existingAlertReport === 'yes' && !existingAlertType) return false;
        if (existingAlertType && !existingAlertNotes.trim()) return false;
        return true;
      }
      if (!newBillability) return false;
      if ((isPaused || isInactive) && newBillability === 'billable') return false;
      if (newBillability === 'billable') {
        if (!newAlertReport) return false;
        if (newAlertReport === 'yes' && !newAlertType) return false;
        if (newAlertType && !newAlertNotes.trim()) return false;
        if (apptKindMode === 'choice' && !newAppointmentKind) return false;
        return true;
      }
      if (newBillability === 'alert_non_billable') {
        if (!newAlertNonBillableType) return false;
        if (!newAlertNotes.trim()) return false;
        return true;
      }
    }
    return false;
  })();

  const handleSubmit = async () => {
    if (!taskTypeId) return;
    const selectedAppointment = selectableAppointments.find((appointment) => appointment.id === appointmentId);
    const moveAppointmentDate = Boolean(
      selectedAppointment
      && !createNewAppointment
      && chosenAppointmentDos
      && originalAppointmentDos
      && chosenAppointmentDos !== originalAppointmentDos,
    );
    if (appointmentId && appointmentDosError) {
      notifications.show({ title: 'Date of service', message: appointmentDosError, color: 'red' });
      return;
    }
    try {
      if (moveAppointmentDate && selectedAppointment) {
        await updateAppointment({
          id: selectedAppointment.id,
          scheduledDate: chosenAppointmentDos,
        }).unwrap();
      }
      try {
        await createTask(buildPayload() as Parameters<typeof createTask>[0]).unwrap();
      } catch (createError) {
        if (moveAppointmentDate && selectedAppointment) {
          try {
            await updateAppointment({
              id: selectedAppointment.id,
              scheduledDate: originalAppointmentDos,
            }).unwrap();
          } catch {
            notifications.show({
              title: 'Appointment date changed',
              message: 'The task was not created, and the appointment date could not be put back.',
              color: 'red',
            });
          }
        }
        throw createError;
      }
      let noShowNote = '';
      if (selectedAppointment && isNoShowStatus(selectedAppointment.status)) {
        noShowNote = await cancelMatchingNoShowTasks({
          listTasks,
          cancelTask,
          patientId,
          deviceId,
          appointmentId: selectedAppointment.id,
        });
      }
      notifications.show({
        title: 'Task created',
        message: noShowNote || 'Task was created successfully.',
        color: noShowNote.startsWith('The No Show task could not') ? 'yellow' : 'teal',
      });
      onClose();
    } catch (err) {
      const data = err && typeof err === 'object' && 'data' in err
        ? (err as { data?: { message?: string; details?: { property: string; message: string[] }[] } }).data
        : undefined;
      const detail = data?.details?.[0];
      const message = detail
        ? `${detail.property}: ${Array.isArray(detail.message) ? detail.message[0] : detail.message}`
        : (data?.message ?? 'Failed to create task.');
      notifications.show({ title: 'Error', message, color: 'red' });
    }
  };

  const BLOCKED_TASK_CODES = new Set([
    'no_show',
    'disconnected',
    'check_in',
    'report_upload',
    'report_upload_with_encounter',
    'update_vendor_site',
    'candidate_patient_enrollment',
    'accession_creation_error',
    'encounter_error',
    'patient_data_verification',
    'schedule_adjustment',
  ]);

  const typeOptions = Object.entries(
    taskTypes
      .filter((t) => !BLOCKED_TASK_CODES.has(t.code))
      .reduce<Record<string, { value: string; label: string }[]>>((acc, t) => {
        const group =
          t.code === 'billing_outreach'
            ? CATEGORY_LABEL.billing
            : (CATEGORY_LABEL[t.workType] ?? t.workType);
        if (!acc[group]) acc[group] = [];
        acc[group].push({ value: t.id, label: t.name });
        return acc;
      }, {}),
  ).map(([group, items]) => ({ group, items }));

  const deviceOptions = selectableDevices.map((d) => ({
    value: d.id,
    label: `${d.type.toUpperCase()} · ${d.modelName}`,
  }));

  const contextLine = selectedDevice
    ? `Opens work for ${patientName}. An open task of the same type is reused for this device.`
    : `Opens work for ${patientName}. An open task of the same type is reused.`;

  const appointmentOptions = [
    ...selectableAppointments.map((a) => {
      const date = a.scheduledDate ? formatAppointmentDay(a.scheduledDate) : null;
      const label = [appointmentTypeLabel(a.type), date, appointmentStatusLabel(a.status)]
        .filter(Boolean)
        .join(' · ');
      return { value: a.id, label };
    }),
    { value: NEW_APPT_VALUE, label: 'No matching appointment — create new' },
  ];

  return (
    <CustomModal
      header={{ title: 'Create Task' }}
      modalProps={{ opened, onClose, size: 'lg' }}
      secondaryButton={{ text: 'Cancel', onClick: onClose }}
      primaryButton={{
        text: 'Create Task',
        onClick: handleSubmit,
        isDisabled: !isSubmittable,
        isLoading: isLoading || isCanceling,
      }}>
      <Stack gap="md" p="md">
        <Text size="sm" c="dimmed">{contextLine}</Text>

        {/* Task type */}
        <Stack gap={4}>
          <Text size="sm" fw={500}>Task type <Text span c="red">*</Text></Text>
          <Select
            placeholder="Select a type..."
            data={typeOptions}
            value={taskTypeId}
            onChange={(v) => { setTaskTypeId(v); resetAll(); setTaskTypeId(v); }}
            searchable
          />
        </Stack>

        {/* Device */}
        {taskTypeId && requiresDeviceSelection && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Device <Text span c="red">*</Text>
            </Text>
            {selectableDevices.length === 0 ? (
              <Text size="sm" c="dimmed">
                {isInactive
                  ? 'No active or inactive device is available. Pending devices cannot be selected.'
                  : 'No active device is available. Inactive and pending devices cannot be selected.'}
              </Text>
            ) : (
            <Select
              placeholder="Select a device..."
              data={deviceOptions}
              value={deviceId}
              onChange={(v) => {
                setDeviceId(v);
                setSetting(null);
                setAppointmentSelection(null);
                setExistingAppointmentDos(null);
                setExistingAlertReport(null);
                setExistingAlertType(null);
                setExistingAlertNotes('');
                setNewBillability(null);
                setNewAlertReport(null);
                setNewAlertType(null);
                setNewAlertNonBillableType(null);
                setNewAlertNotes('');
                setNewAppointmentKind(null);
                setDateOfService(null);
                setNewDateOfService(null);
                setCptCode(null);
              }}
              clearable
            />
            )}
          </Stack>
        )}

        {/* Remote / In clinic — TT tasks only */}
        {deviceId && isTtTask && (
          <Stack gap={6}>
            <Text size="sm" fw={500}>Where was this done? <Text span c="red">*</Text></Text>
            <Radio.Group
              value={setting ?? ''}
              onChange={(v) => {
                if (isPaused && v === 'in_clinic') return;
                setSetting(v as 'remote' | 'in_clinic');
                setAppointmentSelection(null);
                setExistingAppointmentDos(null);
                setExistingAlertReport(null);
                setExistingAlertType(null);
                setExistingAlertNotes('');
                setNewBillability(null);
                setNewAlertReport(null);
                setNewAlertType(null);
                setNewAlertNonBillableType(null);
                setNewAlertNotes('');
                setNewAppointmentKind(null);
                setDateOfService(null);
                setNewDateOfService(null);
                setCptCode(null);
              }}>
              <Group mt={4} gap="lg">
                <Radio value="remote" label="Remote" />
                {isPaused ? (
                  <Tooltip label="Paused patients cannot receive in-clinic tasks" withArrow>
                    <Text size="sm" c="dimmed" style={{ cursor: 'not-allowed' }}>In clinic</Text>
                  </Tooltip>
                ) : (
                  <Radio value="in_clinic" label="In clinic" />
                )}
              </Group>
            </Radio.Group>
          </Stack>
        )}

        {/* ── IN CLINIC ── */}
        {deviceId && isTtTask && setting === 'in_clinic' && (
          <>
            <Stack gap={4}>
              <Text size="sm" fw={500}>Date of service <Text span c="red">*</Text></Text>
              <DatePickerInput
                placeholder="Pick a date"
                value={dateOfService}
                onChange={setDateOfService}
                maxDate={new Date()}
                popoverProps={{ withinPortal: true, zIndex: 400 }}
              />
            </Stack>
            <Stack gap={4}>
              <Text size="sm" fw={500}>In-clinic CPT</Text>
              <Select
                placeholder="Select a CPT..."
                data={IN_CLINIC_CPT_OPTIONS}
                value={cptCode}
                onChange={setCptCode}
                allowDeselect={false}
                comboboxProps={{ withinPortal: true, zIndex: 400 }}
              />
            </Stack>
          </>
        )}

        {/* ── REMOTE ── */}
        {deviceId && isTtTask && setting === 'remote' && (
          <>
            <Stack gap={4}>
              <Text size="sm" fw={500}>Appointment <Text span c="red">*</Text></Text>
              <Text size="xs" c="dimmed">
                No-show appointments, and scheduled or pending appointments dated today. Choosing a no-show appointment cancels the open No Show task.
              </Text>
              <Select
                placeholder="Select an appointment..."
                data={appointmentOptions}
                value={appointmentSelection}
                onChange={(v) => {
                  setAppointmentSelection(v);
                  const appointment = selectableAppointments.find((item) => item.id === v);
                  setExistingAppointmentDos(isoToLocalDate(calendarDateIso(appointment?.scheduledDate)));
                  setExistingAlertReport(null);
                  setExistingAlertType(null);
                  setExistingAlertNotes('');
                  setNewBillability(null);
                  setNewAlertReport(null);
                  setNewAlertType(null);
                  setNewAlertNonBillableType(null);
                  setNewAlertNotes('');
                  setNewAppointmentKind(null);
                  setNewDateOfService(null);
                }}
                allowDeselect={false}
                nothingFoundMessage="No appointments found"
              />
            </Stack>

            {/* ── Existing appointment path ── */}
            {appointmentId && (
              <Stack gap={6}>
                <Stack gap={4}>
                  <Text size="sm" fw={500}>Date of service <Text span c="red">*</Text></Text>
                  <DatePickerInput
                    placeholder="Pick a date"
                    value={existingAppointmentDos}
                    onChange={setExistingAppointmentDos}
                    minDate={isoToLocalDate(originalAppointmentDos) ?? undefined}
                    maxDate={new Date()}
                    error={chosenAppointmentDos ? appointmentDosError : undefined}
                    popoverProps={{ withinPortal: true, zIndex: 400 }}
                  />
                  <Text size="xs" c="dimmed">
                    {originalAppointmentDos
                      ? `From ${formatAppointmentDay(originalAppointmentDos)} through today. A later date updates the appointment, then the task is created for that date.`
                      : 'Through today. The task uses this appointment date.'}
                  </Text>
                </Stack>
                <Text size="sm" fw={500}>Alert report? <Text span c="red">*</Text></Text>
                <Radio.Group
                  value={existingAlertReport ?? ''}
                  onChange={(v) => {
                    setExistingAlertReport(v as 'yes' | 'no');
                    setExistingAlertType(null);
                    setExistingAlertNotes('');
                  }}>
                  <Group mt={4} gap="lg">
                    <Radio value="yes" label="Yes" />
                    <Radio value="no" label="No" />
                  </Group>
                </Radio.Group>

                {existingAlertReport === 'yes' && (
                  <>
                    <Radio.Group
                      value={existingAlertType ?? ''}
                      onChange={(v) => { setExistingAlertType(v as 'red' | 'yellow'); setExistingAlertNotes(''); }}>
                      <Group mt={4} gap="lg">
                        <Radio value="red" label="Red" />
                        <Radio value="yellow" label="Yellow" />
                      </Group>
                    </Radio.Group>
                    {showExistingAlertNotes && (
                      <Textarea
                        label={<Text size="sm" fw={500}>Alert notes <Text span c="red">*</Text></Text>}
                        placeholder="Describe the alert..."
                        minRows={2}
                        autosize
                        value={existingAlertNotes}
                        onChange={(e) => setExistingAlertNotes(e.currentTarget.value)}
                      />
                    )}
                  </>
                )}
              </Stack>
            )}

            {/* ── Create new appointment path ── */}
            {createNewAppointment && (
              <>
                <Stack gap={6}>
                  <Text size="sm" fw={500}>Appointment type <Text span c="red">*</Text></Text>
                  <Radio.Group
                    value={newBillability ?? ''}
                    onChange={(v) => {
                      if ((isPaused || isInactive) && v === 'billable') return;
                      setNewBillability(v as NewApptBillability);
                      setNewAlertReport(null);
                      setNewAlertType(null);
                      setNewAlertNonBillableType(null);
                      setNewAlertNotes('');
                      setNewAppointmentKind(null);
                      setNewDateOfService(null);
                    }}>
                    <Stack mt={4} gap={6}>
                      {isPaused ? (
                        <Tooltip label="Paused patients cannot receive billable tasks" withArrow position="bottom-start">
                          <Text size="sm" c="dimmed" style={{ cursor: 'not-allowed' }}>Billable</Text>
                        </Tooltip>
                      ) : isInactive ? (
                        <Tooltip label="Inactive patients cannot receive billable tasks" withArrow position="bottom-start">
                          <Text size="sm" c="dimmed" style={{ cursor: 'not-allowed' }}>Billable</Text>
                        </Tooltip>
                      ) : (
                        <Radio value="billable" label="Billable" />
                      )}
                      <Radio value="alert_non_billable" label="Alert, non-billable" />
                    </Stack>
                  </Radio.Group>
                </Stack>

                {/* Billable */}
                {newBillability === 'billable' && (
                  <>
                    <Stack gap={4}>
                      <Text size="sm" fw={500}>Date of service <Text span c="red">*</Text></Text>
                      <DatePickerInput
                        placeholder="Pick a date"
                        value={newDateOfService}
                        onChange={setNewDateOfService}
                        maxDate={new Date()}
                        popoverProps={{ withinPortal: true, zIndex: 400 }}
                      />
                    </Stack>
                    <Stack gap={6}>
                      <Text size="sm" fw={500}>Alert report? <Text span c="red">*</Text></Text>
                      <Radio.Group
                        value={newAlertReport ?? ''}
                        onChange={(v) => {
                          setNewAlertReport(v as 'yes' | 'no');
                          setNewAlertType(null);
                          setNewAlertNotes('');
                        }}>
                        <Group mt={4} gap="lg">
                          <Radio value="yes" label="Yes" />
                          <Radio value="no" label="No" />
                        </Group>
                      </Radio.Group>
                      {newAlertReport === 'yes' && (
                        <>
                          <Radio.Group
                            value={newAlertType ?? ''}
                            onChange={(v) => { setNewAlertType(v as 'red' | 'yellow'); setNewAlertNotes(''); }}>
                            <Group mt={4} gap="lg">
                              <Radio value="red" label="Red" />
                              <Radio value="yellow" label="Yellow" />
                            </Group>
                          </Radio.Group>
                          {showNewBillableAlertNotes && (
                            <Textarea
                              label={<Text size="sm" fw={500}>Alert notes <Text span c="red">*</Text></Text>}
                              placeholder="Describe the alert..."
                              minRows={2}
                              autosize
                              value={newAlertNotes}
                              onChange={(e) => setNewAlertNotes(e.currentTarget.value)}
                            />
                          )}
                        </>
                      )}
                    </Stack>
                    {apptKindMode && <Stack gap={6}>
                      <Text size="sm" fw={500}>Appointment kind {apptKindMode === 'choice' && <Text span c="red">*</Text>}</Text>
                      {apptKindMode === 'ilr' && (
                        <Radio.Group value="routine" onChange={() => {}}>
                          <Radio value="routine" label="Routine" readOnly />
                        </Radio.Group>
                      )}
                      {apptKindMode === 'combo' && (
                        <Radio.Group value="combo" onChange={() => {}}>
                          <Radio value="combo" label="Combo" readOnly />
                        </Radio.Group>
                      )}
                      {apptKindMode === 'choice' && (
                        <Radio.Group
                          value={newAppointmentKind ?? ''}
                          onChange={(v) => setNewAppointmentKind(v as 'routine' | 'heart failure')}>
                          <Group mt={4} gap="lg">
                            <Radio value="routine" label="Routine" />
                            <Radio value="heart failure" label="Heart Failure" />
                          </Group>
                        </Radio.Group>
                      )}
                    </Stack>}
                  </>
                )}

                {/* Alert, non-billable */}
                {newBillability === 'alert_non_billable' && (
                  <>
                    <Stack gap={4}>
                      <Text size="sm" fw={500}>Date of service <Text span c="red">*</Text></Text>
                      <DatePickerInput
                        placeholder="Pick a date"
                        value={newDateOfService}
                        onChange={setNewDateOfService}
                        maxDate={new Date()}
                        popoverProps={{ withinPortal: true, zIndex: 400 }}
                      />
                    </Stack>
                    <Stack gap={6}>
                      <Text size="sm" fw={500}>Alert type <Text span c="red">*</Text></Text>
                      <Radio.Group
                        value={newAlertNonBillableType ?? ''}
                        onChange={(v) => {
                          setNewAlertNonBillableType(v as 'red' | 'yellow' | 'physician_request');
                          setNewAlertNotes('');
                        }}>
                        <Group mt={4} gap="lg">
                          <Radio value="red" label="Red" />
                          <Radio value="yellow" label="Yellow" />
                          <Radio value="physician_request" label="Physician request" />
                        </Group>
                      </Radio.Group>
                      {showNewNonBillableAlertNotes && (
                        <Textarea
                          label={<Text size="sm" fw={500}>Alert notes <Text span c="red">*</Text></Text>}
                          placeholder="Describe the alert..."
                          minRows={2}
                          autosize
                          value={newAlertNotes}
                          onChange={(e) => setNewAlertNotes(e.currentTarget.value)}
                        />
                      )}
                    </Stack>
                  </>
                )}
              </>
            )}
          </>
        )}

        {isTransfer && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>Transfer <Text span c="red">*</Text></Text>
            <Radio.Group
              value={transferScope ?? ''}
              onChange={(value) => {
                const next = value as TaskTransferScope;
                setTransferScope(next);
                if (next !== 'internal') setDestinationClinicId(null);
              }}>
              <Stack gap="xs">
                <Radio value="internal" label="Internal transfer (clinic to clinic within our network)" />
                <Radio value="external" label="External transfer (to an unmonitored clinic)" />
              </Stack>
            </Radio.Group>
          </Stack>
        )}

        {isTransfer && transferScope === 'internal' && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>Destination clinic <Text span c="red">*</Text></Text>
            <Select
              placeholder="Search clinics..."
              data={clinicOptions}
              value={destinationClinicId}
              onChange={setDestinationClinicId}
              searchable
              nothingFoundMessage="No clinics"
            />
          </Stack>
        )}

        {/* Subject preview */}
        {taskTypeId && (
          <Text size="sm">
            <Text span c="dimmed">Subject </Text>
            {subject}
          </Text>
        )}

        {isPatientOutreach && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>Request context <Text span c="red">*</Text></Text>
            <Select
              placeholder="Select a request context..."
              data={REQUEST_CONTEXT_OPTIONS}
              value={requestContext}
              onChange={(value) => setRequestContext(value as TaskRequestContext | null)}
            />
          </Stack>
        )}

        {/* Note */}
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Note {(isPatientOutreach || isTransfer) && <Text span c="red">*</Text>}
          </Text>
          <Textarea
            placeholder={isPatientOutreach || isTransfer ? 'Required context for the person who picks this up' : 'Optional context for the person who picks this up'}
            minRows={3}
            autosize
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
        </Stack>
      </Stack>
    </CustomModal>
  );
}
